import { defineStore, acceptHMRUpdate } from 'pinia'
import type { Position } from '@capacitor/geolocation'
import { Geolocation } from '@capacitor/geolocation'
import { DateTime } from 'luxon'
import { LocationsService, ApiError, TrackingType } from '@/api/client'
import { useAuthStore } from '@/stores/AuthStore'
import { v4 as uuidv4 } from 'uuid'
import { Capacitor } from '@capacitor/core'
import { Task } from '@/classes/Task'
import type { Event } from '@/classes/Event'
import { useEmployeeStore } from '@/stores/EmployeeStore'
import { useConfigStore } from './ConfigStore'
import { registerPlugin } from '@capacitor/core'
import type { BackgroundGeolocationPlugin } from '@capacitor-community/background-geolocation'
import { i18n } from '@/locales/locales'
import { chunk } from 'lodash'

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>(
  'BackgroundGeolocation',
)
const { t } = i18n

type TLocation = {
  uuid: string
  longitude: number
  latitude: number
  occured_at: string
  sent_at: string | null
  employee_id?: number | null
}
type TState = {
  locationLog: TLocation[]
  watcherId: string | null
  lastPermissionPromtAt: string | null
}

const initialState: TState = {
  locationLog: [],
  watcherId: null,
  lastPermissionPromtAt: null,
}

const useLocationStore = defineStore('locationStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    currentLocation(state): TLocation | null {
      if (state.watcherId) {
        return state.locationLog[state.locationLog.length - 1] || null
      }
      return null
    },
    isLogging(state): boolean {
      return state.watcherId !== null
    },
  },
  actions: {
    async handleNewEvent(event: Event) {
      if (Capacitor.getPlatform() === 'web') return null
      if (!useConfigStore().trackingEnabled) return null
      if (!(await this._weHavePermissionToTrack())) return null
      const employee = event.employee_id
        ? useEmployeeStore().getEmployeeById(event.employee_id)
        : useEmployeeStore().employee

      // in case of interval trackin we also want to send the activity positions
      if (this._isActivityTracking(event) || this._isIntervalTracking()) {
        const currentLocation = this._createLocation(
          await Geolocation.getCurrentPosition(),
          employee?.id,
        )
        if (currentLocation) {
          try {
            void this._send([currentLocation])
          } catch (_e) {
            this.locationLog.push(currentLocation)
          }
        }
      }
      if (
        this._isIntervalTracking() &&
        ![Task.id.BREAK, Task.id.CLOCKOUT].includes(event.task_id)
      ) {
        void this._startLoggingLocations()
      }
      if ([Task.id.BREAK, Task.id.CLOCKOUT].includes(event.task_id)) {
        this.stopLoggingLocations()
      }
    },

    async _startLoggingLocations() {
      console.log('_startLoggingLocation')
      if (Capacitor.getPlatform() === 'web') return
      this.stopLoggingLocations() // make sure we don't have multiple watchers
      return new Promise(async (resolve) => {
        const watcherId = await BackgroundGeolocation.addWatcher(
          {
            backgroundMessage: t('phrases.gps_is_active'),
            backgroundTitle: t('phrases.gps_is_active'),
            requestPermissions: true,
            distanceFilter: 250,
          },
          (location, error) => {
            if (typeof error == 'object' && error.code === 'NOT_AUTHORIZED') {
              console.error('location tracking was not permitted')
              return
            }

            if (!location) throw new Error('no location received')
            if (!location.time) throw new Error('location.time is undefined')
            const newLocation = {
              uuid: uuidv4(),
              longitude: location.longitude,
              latitude: location.latitude,
              radius: location.accuracy,
              occured_at: DateTime.fromMillis(location.time).toUTC().toISO(),
              sent_at: null,
            }
            this.$state.locationLog.push(newLocation)

            resolve(null)
          },
        )
        this.$state.watcherId = watcherId
      })
    },
    stopLoggingLocations() {
      console.log('_stopLoggingLocations')
      if (this.$state.watcherId) {
        void BackgroundGeolocation.removeWatcher({ id: this.$state.watcherId })
        this.$state.watcherId = null
      }
    },
    _prune() {
      this.$state.locationLog = this.$state.locationLog.filter(
        (location, i, locations) => {
          return location.sent_at === null && i !== locations.length - 1
        },
      )
    },
    _createLocation(position: Position, employeeId?: number): TLocation {
      return {
        uuid: uuidv4(),
        longitude: position.coords.longitude,
        latitude: position.coords.latitude,
        occured_at: DateTime.fromMillis(position.timestamp).toUTC().toISO(),
        sent_at: null,
        employee_id: employeeId,
      }
    },
    async _weHavePermissionToTrack(): Promise<boolean> {
      try {
        if ((await Geolocation.checkPermissions()).location === 'granted')
          return true
        if (
          !this.lastPermissionPromtAt ||
          DateTime.fromISO(this.lastPermissionPromtAt).diffNow('days').days > 14
        ) {
          const newPermissionState = await Geolocation.requestPermissions()
          return newPermissionState.location === 'granted'
        }
        return false
      } catch (_e) {
        // the capacitor plugin throws OS-PLUG-GLOC-0007: Location services are not enabled.
        // we dont need to throw here instead we handle this as if we dont have permission
        return false
      }
    },

    _isActivityTracking(event: Event): boolean {
      // the employees tracking type is only relevant for terminal devices
      // which only allow activity tracking or no tracking.
      // for team and personal devices the tracking type is set in the config store
      return (
        (useAuthStore().isTerminalDevice &&
          event.employee_id &&
          useEmployeeStore().getEmployeeById(event.employee_id)
            ?.tracking_type === TrackingType.ACTIVITY) ||
        useConfigStore().tracking_type === TrackingType.ACTIVITY
      )
    },

    _isIntervalTracking(): boolean {
      // Interval tracking can only be active for team and personal devices
      return (
        (useAuthStore().isTeamDevice || useAuthStore().isPersonalDevice) &&
        useConfigStore().tracking_type === TrackingType.INTERVAL
      )
    },
    sync() {
      console.log('syncing LocationStore')
      const locationsToSend = this.$state.locationLog.filter(
        (location) => location.sent_at === null,
      )
      if (locationsToSend.length === 0) return

      const chunks = chunk(locationsToSend, 50)
      for (const locationChunk of chunks) {
        void this._send(locationChunk)
        this._prune()
      }
    },

    async _send(locations: TLocation[]) {
      try {
        await LocationsService.storeLocations(locations)
        locations.forEach((l) => (l.sent_at = DateTime.now().toUTC().toISO()))
      } catch (error) {
        if (error instanceof ApiError) {
          if (error.status === 401) await useAuthStore().logout()
          else throw new Error(error.message)
        }
      }
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state)
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useLocationStore, import.meta.hot))
}

export { useLocationStore }
